/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from 'react-apollo';
import { useContext } from 'react';

import { usePageContentId } from '@confluence/page-context';
import { type ConfluenceViewState } from '@confluence/comments-panel-queries/entry-points/MarkAllCommentsAsReadMutation';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError';
import { containsNetworkError } from '@confluence/network';
import { setContentErrorAttributes } from '@confluence/monitoring/entry-points/error-attributes';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component';
import { MARK_ALL_COMMENTS_AS_READ_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName';
import { fg } from '@confluence/feature-gating';
import { retry } from '@confluence/retry';

import type {
	useCommentBulkReadMarkAllCommentsAsReadMutation as useCommentBulkReadMarkAllCommentsAsReadMutation$data,
	useCommentBulkReadMarkAllCommentsAsReadMutationVariables as useCommentBulkReadMarkAllCommentsAsReadMutation$variables,
} from './__types__/useCommentBulkReadMarkAllCommentsAsReadMutation';
import { useCommentBulkOperationsActions } from './useCommentBulkOperations';
import { useSyncReadComments } from './useSyncReadComments';
import type { Flags } from './useCommentBulkOperationsFlags';
import { useLocation } from './useLocation';

const useCommentBulkReadMarkAllCommentsAsReadMutation = gql`
	mutation useCommentBulkReadMarkAllCommentsAsReadMutation(
		$cloudId: ID!
		$contentId: String!
		$readView: ConfluenceViewState
	) {
		confluence_markAllCommentsAsRead(
			cloudId: $cloudId
			input: { contentId: $contentId, readView: $readView }
		) {
			success
			errors {
				message
			}
		}
	}
`;
export const useCommentBulkRead = (flags: Flags) => {
	const [contentId = ''] = usePageContentId();
	const { cloudId } = useSessionData();
	const location = useLocation();
	const { setIsBulkReadInProgress } = useCommentBulkOperationsActions();
	const syncReadComments = useSyncReadComments(contentId);
	const experienceTracker = useContext(ExperienceTrackerContext);

	const handleReadAllComments = async () => {
		// Start experience tracking for marking all comments as read
		experienceTracker.start({
			name: MARK_ALL_COMMENTS_AS_READ_EXPERIENCE,
			attributes: {
				pageId: contentId,
			},
		});

		setIsBulkReadInProgress(true);

		if (fg('confluence_frontend_unread_comments_fixes')) {
			let attemptNumber = 1;
			try {
				await retry(
					async (attempt) => {
						attemptNumber = attempt + 1;
						const result = await readAllComments({
							variables: { contentId, cloudId, readView: location as ConfluenceViewState },
						});

						// Check if the mutation succeeded
						if (result?.data?.confluence_markAllCommentsAsRead?.success) {
							// Mark experience as successful
							experienceTracker.succeed({
								name: MARK_ALL_COMMENTS_AS_READ_EXPERIENCE,
								attributes: {
									pageId: contentId,
									attemptNumber,
								},
							});
						} else {
							// Backend returned success: false
							experienceTracker.fail({
								name: MARK_ALL_COMMENTS_AS_READ_EXPERIENCE,
								error: new Error('Backend returned success: false'),
								attributes: {
									attemptNumber,
								},
							});
						}

						return result;
					},
					{},
					async (error) => {
						// Don't retry for expected errors (403/unauthorized, network errors)
						return !isUnauthorizedError(error) && !containsNetworkError(error);
					},
				);
			} catch (error) {
				// Handle errors after retries fail
				// Handle expected errors (403/unauthorized, network errors) - abort experience tracking
				if (isUnauthorizedError(error) || containsNetworkError(error)) {
					experienceTracker.abort({
						name: MARK_ALL_COMMENTS_AS_READ_EXPERIENCE,
						reason: 'Expected error (unauthorized or network error)',
					});
					markErrorAsHandled(error);
				} else {
					// Handle unexpected errors (including 500) - track them with context
					setContentErrorAttributes(error, {
						objectId: contentId,
					});

					getMonitoringClient().submitError(error, {
						attribution: Attribution.COMMENTS,
					});

					markErrorAsHandled(error);

					// Mark experience as failed for unexpected errors
					experienceTracker.fail({
						name: MARK_ALL_COMMENTS_AS_READ_EXPERIENCE,
						error,
						attributes: {
							attemptNumber,
						},
					});
				}

				flags.showFailure(handleReadAllComments);
				setIsBulkReadInProgress(false);
			}
		} else {
			// Legacy behavior when feature flag is disabled
			void readAllComments({
				variables: { contentId, cloudId, readView: location as ConfluenceViewState },
			});
		}
	};

	const [readAllComments] = useMutation<
		useCommentBulkReadMarkAllCommentsAsReadMutation$data,
		useCommentBulkReadMarkAllCommentsAsReadMutation$variables
	>(useCommentBulkReadMarkAllCommentsAsReadMutation, {
		onCompleted: async ({ confluence_markAllCommentsAsRead }) => {
			if (confluence_markAllCommentsAsRead?.success) {
				syncReadComments();
				flags.showSuccess();
			} else {
				flags.showFailure(handleReadAllComments);
			}
			setIsBulkReadInProgress(false);
		},
		onError: (error) => {
			if (fg('confluence_frontend_unread_comments_fixes')) {
				// Handle expected errors (403/unauthorized, network errors) - just mark as handled
				if (isUnauthorizedError(error) || containsNetworkError(error)) {
					markErrorAsHandled(error);
				} else {
					// Handle unexpected errors (including 500) - track them with context
					setContentErrorAttributes(error, {
						objectId: contentId,
					});

					getMonitoringClient().submitError(error, {
						attribution: Attribution.COMMENTS,
					});

					markErrorAsHandled(error);
				}
			}

			flags.showFailure(handleReadAllComments);
			setIsBulkReadInProgress(false);
		},
	});
	return handleReadAllComments;
};
